import React, { createContext, useContext, useEffect } from 'react';
import { useWebRTC, UseWebRTCReturn } from '../../hooks/useWebRTC';

/**
 * Provides a SINGLE shared WebRTC/socket instance for the entire agent session.
 *
 * Why this exists:
 * AgentRegistration, ChatCallingButton, and IncomingCallModal each previously
 * called useWebRTC() independently, creating three separate sockets with three
 * different SIDs. The agent would register on SID_A, but call:incoming events
 * went to SID_A while the call UI was listening on SID_B — they never met.
 *
 * Usage:
 *   <CallingProvider isAgent>
 *     <YourPage />
 *   </CallingProvider>
 *
 *   // Inside any child component:
 *   const { callState, acceptCall, incomingCallData } = useCallingContext();
 */

const CallingContext = createContext<UseWebRTCReturn | null>(null);

export const CallingProvider: React.FC<{
  children: React.ReactNode;
  isAgent?: boolean;
  /** Logged-in agent's user ID — forwarded to the backend for caller/receiver tracking. */
  agentId?: string;
}> = ({ children, isAgent = false, agentId }) => {
  const webrtc = useWebRTC({ autoConnect: true, agentId });

  // Register as agent on mount so the agent is immediately available for calls
  // without needing to open a specific conversation first.
  useEffect(() => {
    if (isAgent) {
      webrtc.registerAsAgent();
      return () => webrtc.unregisterAsAgent();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgent]);

  // Always register as a customer too — this puts every logged-in user in the
  // backend's registered_customers map so they can receive agent-initiated calls
  // regardless of their role (agent, rent-remote, etc.).
  useEffect(() => {
    if (agentId) webrtc.registerAsCustomer(agentId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  return (
    <CallingContext.Provider value={webrtc}>
      {children}
    </CallingContext.Provider>
  );
};

/**
 * Consume the shared WebRTC context.
 * Falls back gracefully (returns null) when used outside CallingProvider
 * so customer-side components can still call useWebRTC() directly.
 */
export const useCallingContext = (): UseWebRTCReturn | null => {
  return useContext(CallingContext);
};
